import { useState, useRef, useEffect } from 'react';
import { Zap, Plus, Trash2, ChevronDown } from 'lucide-react';
import Badge from '../ui/Badge';

/** Opções mockadas para "Adicionar integração". Ponto de integração: buscar da API. */
const DEFAULT_AVAILABLE_INTEGRATIONS = [
  'Pipedrive (CRM)',
  'RD Station',
  'Zendesk Sync',
  'WhatsApp Business',
];

/**
 * Lista de integrações mockadas com badge de status (ativo/inativo).
 *
 * @param {Object} props
 * @param {{ id: string, name: string, status: 'active'|'inactive' }[]} props.integrations
 * @param {(name: string) => void} props.onAddIntegration
 * @param {(integrationId: string, status: 'active'|'inactive') => void} [props.onToggleStatus]
 * @param {(integrationId: string) => void} [props.onRemove]
 * @param {string[]} [props.availableIntegrations]
 */
export default function AgentIntegrations({
  integrations = [],
  onAddIntegration,
  onToggleStatus,
  onRemove,
  availableIntegrations = DEFAULT_AVAILABLE_INTEGRATIONS,
}) {
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!showPicker) return;
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showPicker]);

  const existingNames = new Set(integrations.map((i) => i.name));
  const options = availableIntegrations.filter((name) => !existingNames.has(name));

  const handlePick = (name) => {
    onAddIntegration(name);
    setShowPicker(false);
  };

  return (
    <div className="bg-appSurface p-8 rounded-3xl border border-appSurfaceBorder shadow-sm space-y-6">
      <h3 className="text-xs font-black text-appText uppercase tracking-widest flex items-center gap-2">
        <Zap size={16} className="text-appPrimary" /> Integrações
      </h3>

      <div className="flex flex-wrap gap-2 items-center">
        {integrations.length === 0 ? (
          <p className="text-appTextMuted text-sm py-2 w-full">
            Nenhuma integração. Clique em + para adicionar (ex: Pipedrive, RD Station).
          </p>
        ) : (
          integrations.map((int) => (
            <div
              key={int.id}
              className="group inline-flex items-center gap-2 px-3 py-1.5 bg-appSurfaceBorder/20 text-appText rounded-lg border border-appSurfaceBorder"
            >
              <span className="text-[10px] font-bold">{int.name}</span>
              <Badge
                colorClass={
                  int.status === 'active'
                    ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                    : 'bg-slate-100 text-slate-600 border-slate-200'
                }
              >
                {int.status === 'active' ? 'Ativo' : 'Inativo'}
              </Badge>
              {onToggleStatus && (
                <button
                  type="button"
                  onClick={() =>
                    onToggleStatus(int.id, int.status === 'active' ? 'inactive' : 'active')
                  }
                  className="text-[10px] font-bold text-appPrimary hover:underline"
                  aria-label={`Alternar status de ${int.name}`}
                >
                  {int.status === 'active' ? 'Desativar' : 'Ativar'}
                </button>
              )}
              {onRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(int.id)}
                  className="p-1 text-slate-400 hover:text-rose-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label={`Remover ${int.name}`}
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))
        )}
        <div className="relative" ref={pickerRef}>
          <button
            type="button"
            onClick={() => setShowPicker((v) => !v)}
            className="p-1.5 text-appPrimary hover:bg-appPrimaryLight rounded-lg inline-flex items-center gap-1"
            aria-label="Adicionar integração"
            aria-expanded={showPicker}
            aria-haspopup="listbox"
          >
            <Plus size={16} />
            <ChevronDown size={12} className={showPicker ? 'rotate-180' : ''} />
          </button>
          {showPicker && (
            <ul
              className="absolute left-0 top-full mt-1 py-1 bg-appSurface border border-appSurfaceBorder rounded-xl shadow-lg z-10 min-w-[180px]"
              role="listbox"
            >
              {options.length === 0 ? (
                <li className="px-3 py-2 text-appTextMuted text-xs">Todas já adicionadas</li>
              ) : (
                options.map((name) => (
                  <li key={name}>
                    <button
                      type="button"
                      role="option"
                      onClick={() => handlePick(name)}
                      className="w-full text-left px-3 py-2 text-xs font-medium text-appText hover:bg-appSurfaceBorder/30 rounded-lg"
                    >
                      {name}
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
